import { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Cat, Plus, ChevronLeft, ChevronRight, Settings, ListTodo, Coins, Send, User, GraduationCap, CalendarIcon, Star } from 'lucide-react';
import dynamicIconImports from 'lucide-react/dynamicIconImports';
import { lazy, Suspense } from 'react';
import { format, startOfWeek, addDays } from 'date-fns';
import { he } from 'date-fns/locale';
import { supabase } from '@/integrations/supabase/client';
import { useQueryClient } from '@tanstack/react-query';
import { UserSettingsDialog } from '@/components/Calendar/UserSettingsDialog';
import { useGoogleCalendarEvents } from '@/hooks/useGoogleCalendarEvents';
import { WeeklyTaskSlotCard } from '@/components/Calendar/WeeklyTaskSlotCard';
import { WeeklyTasksManagementDialog } from '@/components/Calendar/WeeklyTasksManagementDialog';
import { AddEventDialog } from '@/components/Calendar/AddEventDialog';
import { EditEventDialog } from '@/components/Calendar/EditEventDialog';
import { useWeeklyTasks, WeeklyTask } from '@/hooks/useWeeklyTasks';
import { useUserCoinBalance, useTransferCoins, useCoinTransferHistory } from '@/hooks/useUserCoinBalance';
import { CoinTransferHistoryDialog } from '@/components/Calendar/CoinTransferHistoryDialog';
import { useUserIcons } from '@/hooks/useUserIcons';
import { useSchoolHolidays } from '@/hooks/useSchoolHolidays';
import { toast } from 'sonner';
import { useAuth } from '@/contexts/AuthContext';
import { useIsMobile } from '@/hooks/use-mobile';
import { MobileCalendarView } from '@/components/Calendar/MobileCalendarView';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Calendar } from '@/components/ui/calendar';
import { cn } from '@/lib/utils';
import { differenceInWeeks, startOfWeek as dateFnsStartOfWeek } from 'date-fns';

const USERS = [
  { id: 'matan', name: 'מתן', color: 'hsl(var(--skylight-blue))', textColor: 'text-skylight-blue' },
  { id: 'heila', name: 'הילה', color: 'hsl(var(--skylight-pink))', textColor: 'text-skylight-pink' },
  { id: 'asaf', name: 'אסף', color: 'hsl(var(--skylight-green))', textColor: 'text-skylight-green' }
];

// Convert PascalCase to kebab-case for dynamic import
const toKebabCase = (str: string) => {
  return str
    .replace(/([a-z])([A-Z])/g, '$1-$2')
    .replace(/([A-Z])([A-Z][a-z])/g, '$1-$2')
    .toLowerCase();
};

// Dynamic icon component
function DynamicIcon({ iconName, className, style }: { iconName: string; className?: string; style?: React.CSSProperties }) {
  const kebabName = toKebabCase(iconName);
  const iconImport = dynamicIconImports[kebabName as keyof typeof dynamicIconImports];
  
  if (!iconImport) {
    return <User className={className} style={style} />;
  }
  
  const LucideIcon = lazy(iconImport);
  
  return (
    <Suspense fallback={<User className={className} style={style} />}>
      <LucideIcon className={className} style={style} />
    </Suspense>
  );
}

const HOURS = Array.from({ length: 17 }, (_, i) => i + 7); // 07:00 - 23:00

// מערך ימים באנגלית לפי אינדקס JS (0=Sunday..6=Saturday)
const EN_DAYS = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];

// מחזיר את שם היום באנגלית לפי Date.getDay()
const getEnglishDayKey = (date: Date) => EN_DAYS[date.getDay()];

// מתרגם ערך עברי לאנגלית אם מגיע (תמיכה בנתונים מעורבים)
const normalizeDay = (value?: string | null) => {
  if (!value) return null;
  const heToEn: Record<string, string> = {
    'ראשון': 'Sunday',
    'שני': 'Monday',
    'שלישי': 'Tuesday',
    'רביעי': 'Wednesday',
    'חמישי': 'Thursday',
    'שישי': 'Friday',
    'שבת': 'Saturday',
  };
  return heToEn[value] || value;
};

export default function WeeklyCalendar() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const isMobile = useIsMobile();
  const { profile } = useAuth();
  const isUserAdmin = profile?.role === 'admin';
  const [selectedUsers, setSelectedUsers] = useState<string[]>(USERS.map(u => u.id));
  const [weekOffset, setWeekOffset] = useState(0);
  const [isTransitioning, setIsTransitioning] = useState(false);
  const [currentTime, setCurrentTime] = useState(new Date());
  const [weather, setWeather] = useState<any>(null);
  const hourRefs = useRef<{ [key: number]: HTMLDivElement | null }>({});
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const [settingsDialogOpen, setSettingsDialogOpen] = useState(false);
  const [selectedUserId, setSelectedUserId] = useState<string>('');
  const [managementDialogOpen, setManagementDialogOpen] = useState(false);
  const [coinHistoryDialogOpen, setCoinHistoryDialogOpen] = useState(false);
  const [addEventDialogOpen, setAddEventDialogOpen] = useState(false);
  const [editEventDialogOpen, setEditEventDialogOpen] = useState(false);
  const [selectedTaskForEdit, setSelectedTaskForEdit] = useState<WeeklyTask | null>(null);
  const [calendarPopoverOpen, setCalendarPopoverOpen] = useState(false);
  
  // Fetch weekly tasks (new system)
  const { data: weeklyTasks = [] } = useWeeklyTasks(weekOffset);
  
  // User icons from settings
  const { getUserIcon } = useUserIcons();
  
  // School holidays
  const { getHolidayForDate, getIsraeliHolidayForDate } = useSchoolHolidays();
  
  // Fetch Matan's coin balance
  const { data: matanBalance } = useUserCoinBalance('matan');
  const transferCoins = useTransferCoins();

  const handleTransferCoins = () => {
    if (matanBalance?.balance && matanBalance.balance > 0) {
      transferCoins.mutate('matan', {
        onSuccess: () => {
          toast.success(`הועברו ${matanBalance.balance} מטבעות!`);
        },
      });
    }
  };
  
  // Fetch Google Calendar events
  const { data: googleEvents = [] } = useGoogleCalendarEvents();

  // Update current time every minute
  useEffect(() => {
    const timer = setInterval(() => {
      setCurrentTime(new Date());
    }, 60000);
    return () => clearInterval(timer);
  }, []);

  // Fetch weather data (using Open-Meteo free API - no key needed)
  // Fetch up to 14 days to cover next week as well
  useEffect(() => {
    const fetchWeather = async () => {
      try {
        // Herzliya coordinates
        const lat = 32.1650;
        const lon = 34.8441;
        const response = await fetch(
          `https://api.open-meteo.com/v1/forecast?latitude=${lat}&longitude=${lon}&daily=temperature_2m_max,temperature_2m_min,weathercode&timezone=auto&forecast_days=14`
        );
        const data = await response.json();
        setWeather(data);
      } catch (error) {
        console.error('Failed to fetch weather:', error);
      }
    };
    fetchWeather();
  }, []);

  // Auto-scroll to current hour on mount and when week changes
  useEffect(() => {
    const currentHour = new Date().getHours();
    const scrollToHour = currentHour >= 7 && currentHour <= 23 ? currentHour : 7;
    
    // Wait for render and transition to complete
    const timer = setTimeout(() => {
      const hourElement = hourRefs.current[scrollToHour];
      const scrollContainer = scrollContainerRef.current;
      
      if (hourElement && scrollContainer) {
        // Calculate the offset from the top of the scroll container
        const containerRect = scrollContainer.getBoundingClientRect();
        const elementRect = hourElement.getBoundingClientRect();
        const offset = elementRect.top - containerRect.top + scrollContainer.scrollTop;
        
        scrollContainer.scrollTo({ 
          top: offset - 100, // Add some padding from top
          behavior: 'smooth' 
        });
      }
    }, isTransitioning ? 300 : 100);

    return () => clearTimeout(timer);
  }, [isTransitioning]);

  // Always scroll to current hour on initial page load
  useEffect(() => {
    const currentHour = new Date().getHours();
    const scrollToHour = currentHour >= 7 && currentHour <= 23 ? currentHour : 7;
    
    const timer = setTimeout(() => {
      const hourElement = hourRefs.current[scrollToHour];
      const scrollContainer = scrollContainerRef.current;
      
      if (hourElement && scrollContainer) {
        const containerRect = scrollContainer.getBoundingClientRect();
        const elementRect = hourElement.getBoundingClientRect();
        const offset = elementRect.top - containerRect.top + scrollContainer.scrollTop;
        
        scrollContainer.scrollTo({ 
          top: offset - 100,
          behavior: 'smooth' 
        });
      }
    }, 300);

    return () => clearTimeout(timer);
  }, []);

  // Real-time subscription for weekly tasks
  useEffect(() => {
    const channel = supabase
      .channel('weekly-calendar-tasks')
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'weekly_task_templates'
        },
        () => {
          queryClient.invalidateQueries({ queryKey: ['weekly-task-templates'] });
        }
      )
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'weekly_task_schedule'
        },
        () => {
          queryClient.invalidateQueries({ queryKey: ['weekly-task-schedules'] });
        }
      )
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'task_completions'
        },
        () => {
          queryClient.invalidateQueries({ queryKey: ['task-completions'] });
        }
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, [queryClient]);

  const weekStart = startOfWeek(new Date(), { weekStartsOn: 0 });
  const currentWeekStart = addDays(weekStart, weekOffset * 7);
  const weekDays = Array.from({ length: 7 }, (_, i) => addDays(currentWeekStart, i));

  const changeWeek = (direction: number) => {
    setIsTransitioning(true);
    setTimeout(() => {
      setWeekOffset(prev => prev + direction);
      setIsTransitioning(false);
    }, 150);
  };

  const goToToday = () => {
    setIsTransitioning(true);
    setTimeout(() => {
      setWeekOffset(0);
      setIsTransitioning(false);
    }, 150);
  };

  const goToDate = (date: Date) => {
    const today = new Date();
    const todayWeekStart = dateFnsStartOfWeek(today, { weekStartsOn: 0 });
    const selectedWeekStart = dateFnsStartOfWeek(date, { weekStartsOn: 0 });
    const weeksDiff = differenceInWeeks(selectedWeekStart, todayWeekStart);
    
    setIsTransitioning(true);
    setTimeout(() => {
      setWeekOffset(weeksDiff);
      setIsTransitioning(false);
      setCalendarPopoverOpen(false);
    }, 150);
  };

  const toggleUser = (userId: string) => {
    setSelectedUsers(prev =>
      prev.includes(userId)
        ? prev.filter(id => id !== userId)
        : [...prev, userId]
    );
  };

  const openUserSettings = (userId: string) => {
    setSelectedUserId(userId);
    setSettingsDialogOpen(true);
  };

  const handleEditTask = (task: WeeklyTask) => {
    setSelectedTaskForEdit(task);
    setEditEventDialogOpen(true);
  };

  const getTasksForDayAndHour = (dayKeyEn: string, hour: number): (WeeklyTask | any)[] => {
    // המשימות מתוזמנות לפי יום בשבוע בלבד (לא תאריך ספציפי)
    const regularTasks = weeklyTasks.filter(task => {
      const dayMatches = normalizeDay(task.scheduled_day) === dayKeyEn;
      const hourMatches = task.scheduled_hour === hour;
      return dayMatches && hourMatches && selectedUsers.includes(task.user_name);
    });

    // Filter Google events for this day and hour  
    const googleEventsForSlot = googleEvents.filter((event: any) => {
      if (!event.start?.dateTime) return false;
      
      const eventStart = new Date(event.start.dateTime);
      const eventDayKey = getEnglishDayKey(eventStart);
      const eventHour = eventStart.getHours();
      
      const user = USERS.find(u => u.id === event.user_name);
      const isUserSelected = user && selectedUsers.includes(user.id);
      
      return eventDayKey === dayKeyEn && 
             eventHour === hour &&
             isUserSelected;
    });

    return [...regularTasks, ...googleEventsForSlot];
  };

  // Get tasks without a specific scheduled day
  const getUnscheduledTasks = (): WeeklyTask[] => {
    return weeklyTasks.filter(task => {
      const hasNoDay = !task.scheduled_day || task.scheduled_day.trim() === '';
      return hasNoDay && selectedUsers.includes(task.user_name);
    }).sort((a, b) => {
      if (a.isCompleted !== b.isCompleted) {
        return a.isCompleted ? 1 : -1;
      }
      return 0;
    });
  };

  // Check if any day has tasks for a given hour
  const hasAnyTasksForHour = (hour: number): boolean => {
    return weekDays.some(day => {
      const dayKeyEn = getEnglishDayKey(day);
      const tasks = getTasksForDayAndHour(dayKeyEn, hour);
      return tasks.length > 0;
    });
  };

  const unscheduledTasks = getUnscheduledTasks();

  // Mobile View
  if (isMobile) {
    return (
      <>
        <MobileCalendarView
          weekOffset={weekOffset}
          currentTime={currentTime}
          selectedUsers={selectedUsers}
          weeklyTasks={weeklyTasks}
          googleEvents={googleEvents}
          matanBalance={matanBalance}
          isUserAdmin={isUserAdmin}
          onChangeWeek={changeWeek}
          onGoToToday={goToToday}
          onGoToDate={goToDate}
          onToggleUser={toggleUser}
          onOpenUserSettings={openUserSettings}
          onOpenManagement={() => setManagementDialogOpen(true)}
          onOpenAddEvent={() => setAddEventDialogOpen(true)}
          onOpenCoinHistory={() => setCoinHistoryDialogOpen(true)}
          onTransferCoins={handleTransferCoins}
          onEditTask={handleEditTask}
          onNavigateToTasks={() => navigate('/tasks')}
          transferCoinsPending={transferCoins.isPending}
        />

        <UserSettingsDialog
          open={settingsDialogOpen}
          onOpenChange={setSettingsDialogOpen}
          userId={selectedUserId}
          userName={USERS.find(u => u.id === selectedUserId)?.name || ''}
          userColor={USERS.find(u => u.id === selectedUserId)?.color || 'hsl(var(--primary))'}
        />
        
        <WeeklyTasksManagementDialog
          open={managementDialogOpen}
          onOpenChange={setManagementDialogOpen}
        />
        
        <CoinTransferHistoryDialog
          open={coinHistoryDialogOpen}
          onOpenChange={setCoinHistoryDialogOpen}
          userName="matan"
        />
        
        <AddEventDialog
          open={addEventDialogOpen}
          onOpenChange={setAddEventDialogOpen}
        />
        
        <EditEventDialog
          open={editEventDialogOpen}
          onOpenChange={setEditEventDialogOpen}
          task={selectedTaskForEdit}
          weekOffset={weekOffset}
          isAdmin={isUserAdmin}
        />
      </>
    );
  }

  // Desktop View
  return (
    <div className="min-h-screen bg-slate-900 animate-fade-in" dir="rtl">
      {/* Header - Compact Design */}
      <div className="bg-slate-800 border-b border-slate-700 px-4 py-2 transition-all duration-300">
        <div className="max-w-[1600px] mx-auto">
          {/* Top Row */}
          <div className="flex items-center justify-between mb-2">
            {/* Left: Date & Time */}
            <div className="flex items-center gap-2">
              {/* תאריכי ימים */}
              <span className="text-base font-semibold text-slate-200">
                {format(currentWeekStart, 'd', { locale: he })}-{format(addDays(currentWeekStart, 6), 'd', { locale: he })}
              </span>
              
              {/* חודש */}
              <span className="text-base font-semibold text-slate-200">
                {format(currentWeekStart, 'MMM', { locale: he })}
              </span>
              
              {/* שנה */}
              <span className="text-base font-semibold text-slate-200">
                {format(currentWeekStart, 'yyyy', { locale: he })}
              </span>
              
              {/* יום */}
              <span className="text-base font-semibold text-slate-200">
                {format(currentTime, 'EEE', { locale: he })}
              </span>
              
              {/* שעה */}
              <span className="text-base font-semibold text-slate-200">
                {format(currentTime, 'HH:mm', { locale: he })}
              </span>
            </div>
            
            {/* Right: Actions */}
            <div className="flex items-center gap-2">
              {/* Week Navigation */}
              <div className="flex items-center gap-1 bg-slate-700 px-2 py-1 rounded">
                <Button 
                  variant="ghost" 
                  size="icon" 
                  className="h-6 w-6 hover:bg-slate-600 text-slate-300"
                  onClick={() => changeWeek(-1)}
                >
                  <ChevronRight className="h-3 w-3" />
                </Button>
                <button 
                  onClick={goToToday}
                  className="text-xs font-medium px-1.5 text-slate-300 hover:text-skylight-blue transition-colors"
                >
                  השבוע
                </button>
                <Popover open={calendarPopoverOpen} onOpenChange={setCalendarPopoverOpen}>
                  <PopoverTrigger asChild>
                    <Button
                      variant="ghost"
                      size="icon"
                      className="h-6 w-6 hover:bg-slate-600 text-slate-300"
                      title="בחר תאריך"
                    >
                      <CalendarIcon className="h-3 w-3" />
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className="w-auto p-0" align="center">
                    <Calendar
                      mode="single"
                      selected={currentWeekStart}
                      onSelect={(date) => date && goToDate(date)}
                      initialFocus
                      className={cn("p-3 pointer-events-auto")}
                      modifiers={{
                        schoolHoliday: (date) => getHolidayForDate(date) !== null,
                        israeliHoliday: (date) => getIsraeliHolidayForDate(date) !== null,
                      }}
                      modifiersClassNames={{
                        schoolHoliday: "bg-amber-500/20 text-amber-400 font-medium",
                        israeliHoliday: "bg-violet-500/20 text-violet-400 font-medium",
                      }}
                    />
                  </PopoverContent>
                </Popover>
                <Button 
                  variant="ghost" 
                  size="icon" 
                  className="h-6 w-6 hover:bg-slate-600 text-slate-300"
                  onClick={() => changeWeek(1)}
                >
                  <ChevronLeft className="h-3 w-3" />
                </Button>
              </div>
              
              <Button 
                size="sm"
                className="bg-skylight-blue hover:bg-skylight-blue/90 text-white rounded-full h-7 px-3"
                onClick={() => setAddEventDialogOpen(true)}
              >
                <Plus className="h-3 w-3 ml-1" />
                <span className="text-xs">אירוע</span>
              </Button>

              {isUserAdmin && (
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setManagementDialogOpen(true)}
                  className="h-7 px-3 bg-slate-700 border-slate-600 text-slate-200 hover:bg-slate-600 transition-all duration-200"
                  title="ניהול משימות חוזרות של מתן"
                >
                  <Cat className="h-3.5 w-3.5 ml-1" />
                  <span className="text-xs">ניהול משימות מתן</span>
                </Button>
              )}
            </div>
          </div>

          {/* Bottom Row - User Filters */}
          <div className="flex items-center gap-2">
            {USERS.map(user => (
              <div 
                key={user.id} 
                className="flex items-center gap-1.5 px-2 py-1 rounded-lg bg-gradient-to-r from-slate-700/50 to-slate-700/30 border border-slate-600/50 shadow-sm"
              >
                <Button
                  variant="ghost"
                  size="icon"
                  onClick={() => openUserSettings(user.id)}
                  className="h-6 w-6 text-slate-400 hover:bg-slate-600"
                  title={`הגדרות יומן - ${user.name}`}
                >
                  <Settings className="h-3 w-3" />
                </Button>
                
                <button
                  onClick={() => toggleUser(user.id)}
                  className={`
                    flex items-center gap-1.5 px-2 py-0.5 rounded-full text-xs
                    transition-all duration-300
                    ${selectedUsers.includes(user.id) 
                      ? 'bg-slate-600 shadow-sm border border-slate-500 text-slate-200' 
                      : 'bg-transparent opacity-40 hover:opacity-60 text-slate-400'
                    }
                  `}
                >
                  {getUserIcon(user.id) ? (
                    <DynamicIcon 
                      iconName={getUserIcon(user.id)!} 
                      className="w-3 h-3" 
                      style={{ color: user.color }}
                    />
                  ) : (
                    <div
                      className="w-2 h-2 rounded-full"
                      style={{ backgroundColor: user.color }}
                    />
                  )}
                  <span className="font-medium">{user.name}</span>
                </button>
                
                {/* Coin balance and transfer button for Matan */}
                {user.id === 'matan' && matanBalance && (
                  <div className="flex items-center gap-1 mr-1">
                    <button
                      onClick={() => setCoinHistoryDialogOpen(true)}
                      className="flex items-center gap-1 px-2 py-0.5 rounded-full bg-amber-100 text-amber-700 text-xs font-bold hover:bg-amber-200 transition-colors cursor-pointer"
                      title="לחץ לצפייה בהיסטוריה"
                    >
                      <Coins className="w-3 h-3" />
                      <span>{matanBalance.balance}</span>
                    </button>
                    {matanBalance.balance > 0 && (
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={handleTransferCoins}
                        disabled={transferCoins.isPending}
                        className="h-6 px-2 text-xs bg-green-100 hover:bg-green-200 text-green-700 rounded-full"
                        title="העבר מטבעות"
                      >
                        <Send className="w-3 h-3 ml-1" />
                        העבר אליי
                      </Button>
                    )}
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Calendar Grid */}
      <div className="w-full px-1 pb-2">
        <div className={`
          bg-slate-800 rounded-lg shadow-sm border border-slate-700 overflow-hidden
          transition-all duration-300 ${isTransitioning ? 'opacity-0 scale-[0.98]' : 'opacity-100 scale-100'}
        `}>
          <div className="flex">
            {/* Main Calendar Area */}
            <div className="flex-1 h-[calc(100vh-140px)] overflow-y-auto scroll-smooth" ref={scrollContainerRef}>
              {/* Days Header */}
              <div className="grid grid-cols-[50px_repeat(7,1fr)] border-b border-slate-700 sticky top-0 bg-slate-800 z-10">
                <div className="p-2"></div>
                {weekDays.map((day, i) => {
                  const isToday = format(day, 'yyyy-MM-dd') === format(new Date(), 'yyyy-MM-dd');
                  const holiday = getHolidayForDate(day);
                  const israeliHoliday = getIsraeliHolidayForDate(day);
                  
                  // Calculate day offset from today for weather forecast
                  const today = new Date();
                  today.setHours(0, 0, 0, 0);
                  const dayDate = new Date(day);
                  dayDate.setHours(0, 0, 0, 0);
                  const dayOffset = Math.floor((dayDate.getTime() - today.getTime()) / (1000 * 60 * 60 * 24));
                  
                  // Get weather for this day if available (forecast is for 14 days)
                  const hasWeather = weather && dayOffset >= 0 && dayOffset <= 13;
                  const weatherCode = hasWeather ? weather.daily?.weathercode?.[dayOffset] : null;
                  const tempMax = hasWeather ? weather.daily?.temperature_2m_max?.[dayOffset] : null;
                  
                  const getWeatherEmoji = (code: number) => {
                    if (code === 0) return '☀️';
                    if (code <= 3) return '⛅';
                    if (code <= 48) return '☁️';
                    if (code <= 67) return '🌧️';
                    if (code <= 77) return '🌨️';
                    if (code <= 82) return '🌧️';
                    return '⛈️';
                  };
                  
                    return (
                      <div key={i} className={`p-3 text-center border-r border-slate-700 transition-all duration-300 ${
                        isToday ? 'bg-skylight-blue/15' : ''
                      }`}>
                        <div className={`text-xs font-medium uppercase tracking-wider mb-1 ${
                          isToday ? 'text-skylight-blue' : 'text-slate-400'
                        }`}>
                          {format(day, 'EEE', { locale: he })}
                        </div>
                        <div className={`
                          text-xl font-light transition-all duration-300
                          ${isToday ? 'text-skylight-blue font-medium scale-110' : 'text-slate-200'}
                        `}>
                          {format(day, 'd', { locale: he })}
                        </div>
                        {hasWeather && weatherCode !== null && tempMax !== null && (
                          <div className="flex items-center justify-center gap-1 mt-1 animate-fade-in">
                            <span className="text-sm">{getWeatherEmoji(weatherCode)}</span>
                            <span className="text-xs text-slate-300">
                              {Math.round(tempMax)}°
                            </span>
                          </div>
                        )}
                        {holiday && (
                          <TooltipProvider>
                            <Tooltip>
                              <TooltipTrigger asChild>
                                <div className="flex items-center justify-center gap-1 mt-1 text-amber-400 cursor-default">
                                  <GraduationCap className="h-3.5 w-3.5" />
                                  <span className="text-xs truncate max-w-[80px]">{holiday.name}</span>
                                </div>
                              </TooltipTrigger>
                              <TooltipContent>
                                <p>{holiday.name}</p>
                              </TooltipContent>
                            </Tooltip>
                          </TooltipProvider>
                        )}
                        {israeliHoliday && (
                          <TooltipProvider>
                            <Tooltip>
                              <TooltipTrigger asChild>
                                <div className="flex items-center justify-center gap-1 mt-1 text-violet-400 cursor-default">
                                  <Star className="h-3.5 w-3.5" />
                                  <span className="text-xs truncate max-w-[80px]">{israeliHoliday.name}</span>
                                </div>
                              </TooltipTrigger>
                              <TooltipContent>
                                <p>{israeliHoliday.name}</p>
                              </TooltipContent>
                            </Tooltip>
                          </TooltipProvider>
                        )}
                      </div>
                    );
                })}
              </div>

              {/* Time Slots */}
              <div className="divide-y divide-slate-700">
              {HOURS.map((hour) => {
                  const isCurrentHour = new Date().getHours() === hour;
                  const hasTasks = hasAnyTasksForHour(hour);
                  return (
                    <div 
                      key={hour} 
                      ref={el => hourRefs.current[hour] = el}
                      className={`grid grid-cols-[50px_repeat(7,1fr)] ${hasTasks ? 'min-h-[100px]' : 'min-h-[32px]'}`}
                    >
                      <div className={`${hasTasks ? 'py-4 px-1' : 'py-1 px-1'} border-l border-slate-700 transition-all duration-300 ${
                        isCurrentHour 
                          ? 'bg-skylight-blue/20 border-r-4 border-r-skylight-blue' 
                          : 'bg-slate-800/50'
                      }`}>
                        <div className={`text-sm font-light text-center transition-all duration-300 ${
                          isCurrentHour 
                            ? 'text-skylight-blue font-medium scale-110' 
                            : 'text-slate-400'
                        }`}>
                          {hour.toString().padStart(2, '0')}:00
                        </div>
                      </div>
                      {weekDays.map((day, dayIndex) => {
                        const dayKeyEn = getEnglishDayKey(day);
                        const dayTasks = getTasksForDayAndHour(dayKeyEn, hour);
                        const isToday = format(day, 'yyyy-MM-dd') === format(new Date(), 'yyyy-MM-dd');
                        
                        return (
                          <div
                            key={dayIndex}
                            className={`${hasTasks ? 'p-2' : 'py-1 px-2'} border-r border-slate-700 hover:bg-slate-700/30 transition-all duration-200 cursor-pointer relative group overflow-hidden min-w-0 ${
                              isToday ? 'bg-skylight-blue/10' : ''
                            }`}
                          >
                            {dayTasks.map((task: any) => {
                              const isGoogleEvent = task.source === 'google';
                              const user = USERS.find(u => u.id === task.user_name);
                              
                              if (isGoogleEvent) {
                                return (
                                  <div
                                    key={`google-${task.id}`}
                                    className="mb-2 p-3 rounded-lg transition-all duration-200 hover:shadow-md hover:scale-[1.02] animate-fade-in opacity-80"
                                    style={{
                                      backgroundColor: `${user?.color}15`,
                                      borderRight: `3px solid ${user?.color}`
                                    }}
                                    title={`${task.summary || task.title} (Google Calendar)`}
                                  >
                                    <div className="flex items-center gap-1.5">
                                      <Cat className="h-3 w-3" style={{ color: user?.color }} />
                                      <div className={`font-medium text-sm ${user?.textColor}`}>
                                        {task.summary || task.title || 'אירוע Google'}
                                      </div>
                                    </div>
                                  </div>
                                );
                              }
                              
                              return (
                                <WeeklyTaskSlotCard
                                  key={task.id}
                                  task={task}
                                  user={user}
                                  hour={hour}
                                  weekOffset={weekOffset}
                                  onEditClick={handleEditTask}
                                />
                              );
                            })}
                            <div className="absolute top-2 left-2 opacity-0 group-hover:opacity-100 transition-all duration-200 transform group-hover:scale-110">
                              <Plus className="h-4 w-4 text-slate-500" />
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  );
                })}
              </div>
            </div>

            {/* Unscheduled Tasks Column - Separate continuous column */}
            <div className="w-36 border-r border-slate-700 bg-slate-800/30 flex flex-col h-[calc(100vh-140px)]">
            {/* Header */}
              <div className="p-3 text-center border-b border-slate-700 bg-slate-800 sticky top-0 z-10">
                <div className="flex items-center justify-center gap-1.5 text-xs font-medium text-skylight-blue uppercase tracking-wider mb-1">
                  <ListTodo className="h-4 w-4" />
                  <span>לבצע השבוע</span>
                </div>
                <div className="text-xl font-light text-slate-200">
                  {unscheduledTasks.length}
                </div>
                <div className="flex items-center justify-center gap-1 mt-1">
                  <span className="text-xs text-slate-400">משימות</span>
                </div>
              </div>
              
              {/* Tasks List */}
              <div className="flex-1 overflow-y-auto p-3 space-y-2">
                {unscheduledTasks.length === 0 ? (
                  <div className="text-center text-slate-500 text-sm py-4">
                    אין משימות ללא יום
                  </div>
                ) : (
                  unscheduledTasks.map((task) => {
                    const user = USERS.find(u => u.id === task.user_name);
                    return (
                      <WeeklyTaskSlotCard
                        key={task.id}
                        task={task}
                        user={user}
                        hour={null}
                        weekOffset={weekOffset}
                        onEditClick={handleEditTask}
                      />
                    );
                  })
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
      
      <UserSettingsDialog
        open={settingsDialogOpen}
        onOpenChange={setSettingsDialogOpen}
        userId={selectedUserId}
        userName={USERS.find(u => u.id === selectedUserId)?.name || ''}
        userColor={USERS.find(u => u.id === selectedUserId)?.color || 'hsl(var(--primary))'}
      />
      
      <WeeklyTasksManagementDialog
        open={managementDialogOpen}
        onOpenChange={setManagementDialogOpen}
      />
      
      <CoinTransferHistoryDialog
        open={coinHistoryDialogOpen}
        onOpenChange={setCoinHistoryDialogOpen}
        userName="matan"
      />
      
      <AddEventDialog
        open={addEventDialogOpen}
        onOpenChange={setAddEventDialogOpen}
      />
      
      <EditEventDialog
        open={editEventDialogOpen}
        onOpenChange={setEditEventDialogOpen}
        task={selectedTaskForEdit}
        weekOffset={weekOffset}
        isAdmin={isUserAdmin}
      />
    </div>
  );
}
